import { create } from 'zustand';
import { apiClient } from '../api/config';
import { endpoints } from '../api/endpoints';
import { authService } from '../api/services';

// Helper function to parse JWT token
const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

// Helper to check if token is expired
const isTokenExpired = (token) => {
  const decodedToken = parseJwt(token);
  if (!decodedToken) return true;
  return decodedToken.exp * 1000 < Date.now();
};

// Initialize state from localStorage
const initAuthState = () => {
  const token = localStorage.getItem('jms_token');
  const refreshToken = localStorage.getItem('jms_refresh_token');

  if (!token) {
    return { user: null, isAuthenticated: false };
  }

  // Check if token is expired
  if (isTokenExpired(token)) {
    return { user: null, isAuthenticated: false };
  }

  // Parse user data from token
  const decodedToken = parseJwt(token);
  if (!decodedToken) {
    return { user: null, isAuthenticated: false };
  }

  // Create user object from token data
  const user = {
    id: decodedToken.id,
    email: decodedToken.email,
    firstName: decodedToken.firstName,
    lastName: decodedToken.lastName,
    profilePicture: decodedToken.profilePicture,
    roles: decodedToken.roles || (decodedToken.role ? [decodedToken.role] : ['AUTHOR'])
  };

  return { user, isAuthenticated: true };
};

const useAuthStore = create((set, get) => ({
  ...initAuthState(),
  isLoading: false,
  isRefreshing: false, // Separate flag for background token refresh
  error: null,
  refreshTimer: null,

  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(credentials);
      const { accessToken, refreshToken, user } = response;

      // Store tokens in localStorage
      localStorage.setItem('jms_token', accessToken);
      if (refreshToken) {
        localStorage.setItem('jms_refresh_token', refreshToken);
      }

      // Parse roles from token if user object doesn't have roles
      const userData = user.roles ? user : { ...user, roles: [user.role] };

      set({ user: userData, isAuthenticated: true, isLoading: false });

      // Start token refresh timer
      get().startTokenRefresh();

      return response;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Login failed', isLoading: false });
      throw error;
    }
  },

  logout: () => {
    // Stop token refresh
    get().stopTokenRefresh();

    // Clear tokens from localStorage
    localStorage.removeItem('jms_token');
    localStorage.removeItem('jms_refresh_token');

    // Try to call logout API, but don't wait for it
    try {
      authService.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    set({ user: null, isAuthenticated: false, error: null });
  },

  register: async (registrationData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.register(registrationData);
      const { token, refreshToken, user } = response;

      // Store tokens in localStorage
      localStorage.setItem('jms_token', token);
      if (refreshToken) {
        localStorage.setItem('jms_refresh_token', refreshToken);
      }

      // Parse roles from token if user object doesn't have roles
      const userData = user.roles ? user : { ...user, roles: [user.role] };

      set({ user: userData, isAuthenticated: true, isLoading: false });

      // Start token refresh timer
      get().startTokenRefresh();

      return response;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Registration failed', isLoading: false });
      throw error;
    }
  },

  loadUser: async () => {
    set({ isLoading: true, error: null });
    try {
      const userData = await authService.getProfile();
      const userWithRoles = userData.roles ? userData : { ...userData, roles: [userData.role] };
      set({ user: userWithRoles, isAuthenticated: true, isLoading: false });
      return userWithRoles;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to load user', isLoading: false });
      throw error;
    }
  },

  updateProfile: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.put(endpoints.auth.profile, data);
      const userWithRoles = response.data.roles ? response.data : { ...response.data, roles: [response.data.role] };
      set({ user: userWithRoles, isLoading: false });
      return userWithRoles;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to update profile', isLoading: false });
      throw error;
    }
  },

  refreshToken: async (isBackgroundRefresh = false) => {
    try {
      const refreshToken = localStorage.getItem('jms_refresh_token');
      if (!refreshToken) return false;

      // Prevent multiple refresh attempts
      if (get().isRefreshing || get().isLoading) return false;

      // Use isRefreshing for background refresh to avoid triggering re-renders
      if (isBackgroundRefresh) {
        set({ isRefreshing: true });
      } else {
        set({ isLoading: true });
      }

      const response = await authService.refreshToken(refreshToken);
      const { accessToken, refreshToken: newRefreshToken, user } = response;

      localStorage.setItem('jms_token', accessToken);
      if (newRefreshToken) {
        localStorage.setItem('jms_refresh_token', newRefreshToken);
      }

      // Update user data if provided (silently for background refresh)
      if (user) {
        const userWithRoles = user.roles ? user : { ...user, roles: [user.role] };
        if (isBackgroundRefresh) {
          // For background refresh, update user but don't trigger isLoading
          set({ user: userWithRoles, isAuthenticated: true, isRefreshing: false });
        } else {
          set({ user: userWithRoles, isAuthenticated: true, isLoading: false });
        }
      } else {
        if (isBackgroundRefresh) {
          set({ isRefreshing: false });
        } else {
          set({ isLoading: false });
        }
      }

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (isBackgroundRefresh) {
        set({ isRefreshing: false });
      } else {
        set({ isLoading: false });
      }
      // If refresh fails, logout the user
      useAuthStore.getState().logout();
      return false;
    }
  },

  // Add method to start token refresh interval
  startTokenRefresh: () => {
    // Clear any existing timer
    if (get().refreshTimer) {
      clearInterval(get().refreshTimer);
    }

    // Set up new refresh interval (10 minutes)
    const timer = setInterval(async () => {
      const token = localStorage.getItem('jms_token');
      if (token && !isTokenExpired(token)) {
        // Pass true to indicate this is a background refresh
        await get().refreshToken(true);
      }
    }, 10 * 60 * 1000);

    set({ refreshTimer: timer });
  },

  // Add method to stop token refresh
  stopTokenRefresh: () => {
    if (get().refreshTimer) {
      clearInterval(get().refreshTimer);
      set({ refreshTimer: null });
    }
  },

  changePassword: async (passwordData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.changePassword(passwordData);
      set({ isLoading: false });
      return response;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to change password', isLoading: false });
      throw error;
    }
  },
}));

export default useAuthStore;
